import {
  groupConversationRenderableUnits,
  type PendingApprovalResolutionType,
  parseMessageArtifacts,
} from "#src/view-models/investigations";
import { Button, Skeleton } from "@validereinc/common-components";
import {
  InvestigationMessageRole,
  InvestigationMessageKind,
  InvestigationStatus,
  type InvestigationMessageType,
  type InvestigationStatusType,
} from "@validereinc/domain";
import classNames from "classnames/bind";
import React from "react";
import { CopyableConversationMessageBubble } from "./CopyableConversationMessageBubble";
import {
  ConversationMessageBubbleContent,
  shouldRenderConversationMessageBubble,
} from "./ConversationFeedMessageContent";
import { MessageArtifactList } from "./MessageArtifactList";
import { type PendingApprovalCardDecisionType } from "./PendingApprovalCard";
import styles from "./InvestigationDetailPage.module.scss";

const cx = classNames.bind(styles);

export type ConversationFeedProps = {
  isLoading?: boolean;
  messages: InvestigationMessageType[];
  status: InvestigationStatusType;
  report: string | null;
  pendingApprovalMessageId?: string | null;
  resolutionsByPendingApprovalId?: Map<string, PendingApprovalResolutionType>;
  isApprovalLoading?: boolean;
  hasOlderMessages?: boolean;
  isLoadingOlderMessages?: boolean;
  onApprovalDecision?: (decision: PendingApprovalCardDecisionType) => void;
  onLoadOlder?: () => void;
};

const CONVERSATION_SKELETON_PLACEHOLDERS = [
  InvestigationMessageRole.USER,
  InvestigationMessageRole.AGENT,
  InvestigationMessageRole.USER,
] as const;

const ConversationFeedSkeleton = () => {
  return (
    <div
      className={cx("conversation-feed")}
      data-testid="conversation-skeleton"
      aria-busy
      aria-label="Loading conversation"
    >
      {CONVERSATION_SKELETON_PLACEHOLDERS.map((role, placeholderIndex) => (
        <div
          key={`${role}-${placeholderIndex}`}
          className={cx("conversation-feed__entry", {
            "conversation-feed__entry--user":
              role === InvestigationMessageRole.USER,
            "conversation-feed__entry--agent":
              role === InvestigationMessageRole.AGENT,
          })}
        >
          <Skeleton isLoading>
            <div
              className={cx(
                "conversation-feed__bubble",
                "conversation-feed__bubble--skeleton",
                {
                  "conversation-feed__bubble--user":
                    role === InvestigationMessageRole.USER,
                  "conversation-feed__bubble--agent":
                    role === InvestigationMessageRole.AGENT,
                }
              )}
            />
          </Skeleton>
        </div>
      ))}
    </div>
  );
};

const NO_REPORT_PLACEHOLDER = "No report produced.";

export const ConversationFeed = ({
  isLoading = false,
  messages,
  status,
  report,
  pendingApprovalMessageId = null,
  resolutionsByPendingApprovalId = new Map(),
  isApprovalLoading = false,
  hasOlderMessages = false,
  isLoadingOlderMessages = false,
  onApprovalDecision,
  onLoadOlder,
}: ConversationFeedProps) => {
  const visibleMessages = messages.filter(
    (message) =>
      !(
        message.kind === InvestigationMessageKind.APPROVAL_DECISION &&
        !message.content?.trim()
      )
  );
  const renderableUnits = groupConversationRenderableUnits(messages);
  const hasAnyAgentMessage = visibleMessages.some(
    (message) => message.role === InvestigationMessageRole.AGENT
  );
  const showNoReportPlaceholder =
    status === InvestigationStatus.DONE && !report && !hasAnyAgentMessage;

  if (isLoading) {
    return <ConversationFeedSkeleton />;
  }

  return (
    <div
      className={cx("conversation-feed")}
      data-testid="conversation-feed"
    >
      {hasOlderMessages ? (
        <div className={cx("conversation-feed__load-older")}>
          <Button
            variant="outline"
            size="small"
            isLoading={isLoadingOlderMessages}
            onClick={onLoadOlder}
          >
            Load older messages
          </Button>
        </div>
      ) : null}
      {renderableUnits.map((unit) => {
        const message = unit.message;
        const resolution = resolutionsByPendingApprovalId.get(message.id);
        const isUserMessage = message.role === InvestigationMessageRole.USER;
        const shouldRenderTextBubble =
          shouldRenderConversationMessageBubble(message);
        const messageRenderContext = {
          pendingApprovalMessageId,
          resolution,
          isApprovalLoading,
          onApprovalDecision,
        };

        if (unit.type === "agent") {
          return (
            <div
              key={unit.message.id}
              className={cx(
                "conversation-feed__entry",
                "conversation-feed__entry--agent"
              )}
              data-testid={`conversation-entry-${unit.message.id}`}
            >
              {unit.message.kind ===
              InvestigationMessageKind.PENDING_APPROVAL ? (
                <ConversationMessageBubbleContent
                  message={unit.message}
                  {...messageRenderContext}
                />
              ) : shouldRenderTextBubble ? (
                <CopyableConversationMessageBubble
                  content={unit.message.content}
                  isUserMessage={false}
                  bubbleClassName={cx(
                    "conversation-feed__bubble",
                    "conversation-feed__bubble--agent"
                  )}
                  testId={`conversation-message-${unit.message.role}`}
                >
                  <ConversationMessageBubbleContent
                    message={unit.message}
                    {...messageRenderContext}
                  />
                </CopyableConversationMessageBubble>
              ) : null}
              <MessageArtifactList artifacts={unit.artifacts} />
            </div>
          );
        }

        const artifacts = parseMessageArtifacts(message.metadata);

        return (
          <div
            key={message.id}
            className={cx("conversation-feed__entry", {
              "conversation-feed__entry--user": isUserMessage,
              "conversation-feed__entry--agent":
                message.role === InvestigationMessageRole.AGENT,
            })}
            data-testid={`conversation-entry-${message.id}`}
          >
            {message.kind === InvestigationMessageKind.PENDING_APPROVAL ? (
              <ConversationMessageBubbleContent
                message={message}
                {...messageRenderContext}
              />
            ) : shouldRenderTextBubble ? (
              <CopyableConversationMessageBubble
                content={message.content}
                isUserMessage={isUserMessage}
                bubbleClassName={cx("conversation-feed__bubble", {
                  "conversation-feed__bubble--user": isUserMessage,
                  "conversation-feed__bubble--agent":
                    message.role === InvestigationMessageRole.AGENT,
                })}
                testId={`conversation-message-${message.role}`}
              >
                <ConversationMessageBubbleContent
                  message={message}
                  {...messageRenderContext}
                />
              </CopyableConversationMessageBubble>
            ) : null}
            <MessageArtifactList artifacts={artifacts} />
          </div>
        );
      })}
      {showNoReportPlaceholder ? (
        <div
          className={cx(
            "conversation-feed__bubble",
            "conversation-feed__bubble--agent",
            "conversation-feed__placeholder"
          )}
        >
          {NO_REPORT_PLACEHOLDER}
        </div>
      ) : null}
    </div>
  );
};
